import React, { useEffect, useState } from "react";
import { useApp } from "@/context/AppContext";
import { DEFAULT_THEME, PRESET_THEMES } from "@/data/mock";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { useToast } from "@/hooks/use-toast";
import { Check, Palette, RotateCcw, Database, HardDrive, Loader2 } from "lucide-react";
import { adminGetSettings, adminPatchSettings } from "@/lib/api";

const FIELDS = [
  { k: "primary", label: "Culoare principală" },
  { k: "primaryHover", label: "Hover principală" },
  { k: "accent", label: "Accent" },
  { k: "background", label: "Fundal site" },
  { k: "headerBg", label: "Fundal header" },
  { k: "surface", label: "Fundal carduri" },
  { k: "surfaceAlt", label: "Fundal secundar" },
  { k: "border", label: "Borduri" },
  { k: "text", label: "Text principal" },
  { k: "textMuted", label: "Text secundar" },
];

export default function AdminTheme() {
  const { setTheme: applyTheme, refreshPublic } = useApp();
  const { toast } = useToast();
  const [settings, setSettings] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => { adminGetSettings().then((s) => { setSettings(s); applyTheme(s.theme); }); }, [applyTheme]);

  if (!settings) return <div className="py-10 text-center"><Loader2 className="w-7 h-7 mx-auto animate-spin" style={{ color: "var(--amd-primary)" }} /></div>;

  const theme = settings.theme;
  const updateField = (k, v) => {
    const t = { ...theme, [k]: v };
    setSettings({ ...settings, theme: t });
    applyTheme(t);
  };

  const persist = async (patch) => {
    setSaving(true);
    try {
      const updated = await adminPatchSettings(patch);
      setSettings(updated);
      applyTheme(updated.theme);
      await refreshPublic();
    } finally { setSaving(false); }
  };

  const saveTheme = () => persist({ theme }).then(() => toast({ title: "Temă salvată" }));
  const applyPreset = (p) => { setSettings({ ...settings, theme: p }); applyTheme(p); persist({ theme: p }).then(() => toast({ title: "Temă aplicată", description: p.name })); };
  const resetTheme = () => { setSettings({ ...settings, theme: DEFAULT_THEME }); applyTheme(DEFAULT_THEME); persist({ theme: DEFAULT_THEME }).then(() => toast({ title: "Temă resetată" })); };

  const setStorage = (mode) => persist({ image_storage: mode }).then(() => toast({ title: "Stocare imagini modificată", description: mode === "db" ? "MongoDB (base64)" : "Disc server" }));

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-2xl md:text-3xl font-black text-white flex items-center gap-2"><Palette className="w-6 h-6" style={{ color: "var(--amd-primary)" }} /> Teme & Stocare</h1>
          <p className="text-sm" style={{ color: "var(--amd-text-muted)" }}>Modifică aspectul site-ului și unde stocăm imaginile.</p>
        </div>
        <div className="flex gap-2">
          <Button onClick={resetTheme} variant="outline" className="border-white/20 text-white hover:bg-white/5 gap-2"><RotateCcw className="w-4 h-4" /> Resetează</Button>
          <Button onClick={saveTheme} disabled={saving} className="amd-btn-primary gap-2">{saving ? "Se salvează…" : "Salvează tema"}</Button>
        </div>
      </div>

      <div className="amd-card rounded-lg p-5">
        <h2 className="font-bold text-white mb-3 flex items-center gap-2"><Database className="w-4 h-4" /> Stocare imagini</h2>
        <p className="text-xs mb-3" style={{ color: "var(--amd-text-muted)" }}>
          Imaginile existente își păstrează locația inițială. Setarea afectează doar imaginile noi.
        </p>
        <div className="grid sm:grid-cols-2 gap-3">
          <button onClick={() => setStorage("db")} className="amd-card rounded-lg p-4 text-left flex items-start gap-3 relative" style={{ borderColor: settings.image_storage === "db" ? "var(--amd-primary)" : undefined }}>
            <Database className="w-5 h-5 mt-0.5" style={{ color: "var(--amd-primary)" }} />
            <div>
              <div className="font-bold text-white">MongoDB (base64)</div>
              <div className="text-xs mt-1" style={{ color: "var(--amd-text-muted)" }}>Simplu, portabil. Recomandat pentru început.</div>
            </div>
            {settings.image_storage === "db" && <Check className="absolute top-2 right-2 w-4 h-4" style={{ color: "var(--amd-primary)" }} />}
          </button>
          <button onClick={() => setStorage("disk")} className="amd-card rounded-lg p-4 text-left flex items-start gap-3 relative" style={{ borderColor: settings.image_storage === "disk" ? "var(--amd-primary)" : undefined }}>
            <HardDrive className="w-5 h-5 mt-0.5" style={{ color: "var(--amd-primary)" }} />
            <div>
              <div className="font-bold text-white">Disc server</div>
              <div className="text-xs mt-1" style={{ color: "var(--amd-text-muted)" }}>Mai eficient pentru multe imagini mari. Salvate în /app/backend/uploads.</div>
            </div>
            {settings.image_storage === "disk" && <Check className="absolute top-2 right-2 w-4 h-4" style={{ color: "var(--amd-primary)" }} />}
          </button>
        </div>
      </div>

      <div>
        <h2 className="font-bold text-white mb-3">Teme prestabilite</h2>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
          {PRESET_THEMES.map((p) => {
            const active = p.name === theme.name;
            return (
              <button key={p.name} onClick={() => applyPreset(p)} className="amd-card rounded-lg p-3 text-left relative">
                <div className="flex items-center gap-1.5">
                  <Swatch color={p.primary} /><Swatch color={p.background} /><Swatch color={p.surface} /><Swatch color={p.text} />
                </div>
                <div className="mt-2 font-bold text-white text-sm">{p.name}</div>
                {active && <Check className="absolute top-2 right-2 w-4 h-4" style={{ color: "var(--amd-primary)" }} />}
              </button>
            );
          })}
        </div>
      </div>

      <div className="amd-card rounded-lg p-5">
        <h2 className="font-bold text-white mb-4">Personalizare avansată</h2>
        <div className="grid sm:grid-cols-2 gap-4">
          {FIELDS.map((f) => (
            <div key={f.k}>
              <Label className="text-white text-xs uppercase tracking-wider">{f.label}</Label>
              <div className="mt-1.5 flex items-center gap-2">
                <input type="color" value={theme[f.k]} onChange={(e) => updateField(f.k, e.target.value)} className="w-12 h-11 rounded border cursor-pointer" style={{ borderColor: "var(--amd-border)", background: "var(--amd-surface-alt)" }} />
                <Input value={theme[f.k]} onChange={(e) => updateField(f.k, e.target.value)} className="amd-input h-11 font-mono uppercase" />
              </div>
            </div>
          ))}
        </div>
        <p className="text-xs mt-3" style={{ color: "var(--amd-text-muted)" }}>Schimbările sunt previzualizate live. Apăsă <strong className="text-white">Salvează tema</strong> ca să le aplici pentru toți vizitatorii.</p>
      </div>

      <div className="amd-card rounded-lg p-5">
        <h2 className="font-bold text-white mb-3">Previzualizare</h2>
        <div className="flex flex-wrap items-center gap-3">
          <Button className="amd-btn-primary h-10 px-4">Buton principal</Button>
          <Button variant="outline" className="border-white/20 text-white hover:bg-white/5 h-10">Buton secundar</Button>
          <span className="px-3 py-1 rounded text-xs font-bold text-white" style={{ background: "var(--amd-primary)" }}>Badge promovat</span>
          <a className="amd-link text-sm font-semibold" href="#">Link exemplu</a>
        </div>
      </div>
    </div>
  );
}

function Swatch({ color }) {
  return <span className="inline-block w-5 h-5 rounded-full border" style={{ background: color, borderColor: "rgba(255,255,255,0.15)" }} />;
}
